import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Typography,
} from "@mui/material";

import { ITemplateData } from "@framework/types";

import { useAlarmPaletteResolver } from "../../../../../../hooks/use-alarm-color";
import { ALARMS } from "../../../../../../utils";
import { palette } from "../bar/utils";

interface IProcessedTableRow {
  name: string;
  order: number;
  rowLabel: string;
  cells: Record<string, ITemplateData>;
}

interface IProcessedTableColumn {
  name: string;
  order: number;
  groupLayerName?: string;
}

interface IProcessedTableData {
  DiagramName?: string;
  columns: IProcessedTableColumn[];
  rows: IProcessedTableRow[];
  order: number;
}

const processDataForTable = (data: ITemplateData[]): IProcessedTableData[] => {
  const diagrams: Record<string, IProcessedTableData> = {};

  data.forEach(item => {
    const diagramKey = item.DiagramNum?.toString() || "default";

    if (!diagrams[diagramKey]) {
      diagrams[diagramKey] = {
        DiagramName: item.DiagramName,
        columns: [],
        rows: [],
        order: item.DiagramNum ? parseInt(item.DiagramNum) : 0,
      };
    }

    const columnName = item.LayerName || (item.LayerNum ? String(item.LayerNum) : "");
    const groupLayerName = item.GrpLayerName1;

    const columnKey = `${columnName}__${groupLayerName || ""}`;

    if (
      columnName &&
      !diagrams[diagramKey].columns.some(
        column => column.name === columnName && column.groupLayerName === groupLayerName,
      )
    ) {
      diagrams[diagramKey].columns.push({
        name: columnName,
        order: item.LayerNum || 0,
        groupLayerName,
      });
    }

    const rowKey = `${item.ValueNum}`;
    let row = diagrams[diagramKey].rows.find(r => r.name === rowKey);
    if (!row) {
      row = {
        name: rowKey,
        order: item.ValueNum ? parseInt(item.ValueNum) : 0,
        rowLabel: item.ValueName || (item.AlarmStatus ? ALARMS[item.AlarmStatus] : null) || item.DiagramName || "",
        cells: {},
      };
      diagrams[diagramKey].rows.push(row);
    }

    row.cells[columnKey] = item;
  });

  Object.values(diagrams).forEach(diagram => {
    diagram.columns.sort((a, b) => a.order - b.order);
    diagram.rows.sort((a, b) => a.order - b.order);
  });

  return Object.values(diagrams).sort((a, b) => a.order - b.order);
};

export const AnalyticsTable: React.FC<{ data: ITemplateData[] }> = ({ data }) => {
  const theme = useTheme();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const tableData = processDataForTable(data);

  return (
    <Box sx={{ mx: 4, maxWidth: "100%", overflowX: "auto" }}>
      {tableData.map(diagramData => {
        const groups: Record<string, IProcessedTableColumn[]> = {};
        diagramData.columns.forEach(col => {
          const group = col.groupLayerName || "";
          if (!groups[group]) groups[group] = [];
          groups[group].push(col);
        });
        const groupLastIndexes: number[] = [];
        let colIdx = 0;
        Object.values(groups).forEach((cols, groupIdx, arr) => {
          colIdx += cols.length - 1;
          if (groupIdx !== arr.length - 1) {
            groupLastIndexes.push(colIdx);
          }
          colIdx += 1;
        });
        const hasGroups = diagramData.columns.some(col => col.groupLayerName !== undefined);

        return (
          <Box
            sx={{ display: "flex", flexDirection: "column", justifyContent: "center", mb: 2 }}
            key={`${diagramData.DiagramName}_${diagramData.order}`}
          >
            {hasGroups && (
              <Typography variant="h5" sx={{ mb: 1, maxWidth: "80%", mx: "auto", textWrap: "balance" }}>
                {diagramData.DiagramName}
              </Typography>
            )}
            <TableContainer component={Paper} sx={{ width: "100%" }}>
              <Table sx={{ minWidth: 650 }} aria-label={`table-${diagramData.DiagramName}`}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        minWidth: 260,
                        borderRight: `2px solid ${theme.palette.divider}`,
                        background: theme.palette.grey[100],
                        fontWeight: 600,
                        verticalAlign: "middle",
                      }}
                      {...(hasGroups ? { rowSpan: 2, align: "center" } : { align: "center" })}
                    >
                      {hasGroups ? data.find(item => item.GrpHeader1)?.GrpHeader1 || "" : diagramData.DiagramName}
                    </TableCell>
                    {hasGroups &&
                      Object.entries(groups).map(([group, cols], idx, arr) => (
                        <TableCell
                          key={group}
                          align="center"
                          colSpan={cols.length}
                          sx={{
                            borderRight: idx !== arr.length - 1 ? `2px solid ${theme.palette.divider}` : undefined,
                            background: theme.palette.grey[100],
                            fontWeight: 600,
                          }}
                        >
                          {group || ""}
                        </TableCell>
                      ))}
                    {!hasGroups &&
                      diagramData.columns.map((column, idx, arr) => (
                        <TableCell
                          key={`${column.name}__${column.groupLayerName || ""}`}
                          sx={{
                            borderRight: idx !== arr.length - 1 ? `1px solid ${theme.palette.grey[300]}` : undefined,
                            background: theme.palette.grey[50],
                            fontWeight: 500,
                          }}
                        >
                          {column.name}
                        </TableCell>
                      ))}
                  </TableRow>
                  {hasGroups && (
                    <TableRow>
                      {diagramData.columns.map((column, idx, arr) => (
                        <TableCell
                          key={`${column.name}__${column.groupLayerName || ""}`}
                          sx={{
                            borderRight: groupLastIndexes.includes(idx)
                              ? `2px solid ${theme.palette.divider}`
                              : idx !== arr.length - 1
                                ? `1px solid ${theme.palette.grey[300]}`
                                : undefined,
                            background: theme.palette.grey[50],
                            fontWeight: 500,
                          }}
                        >
                          {column.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {diagramData.rows.map((row, idx) => (
                    <TableRow key={`${row.name}_${idx}`} sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}` }}>
                      <TableCell
                        sx={{
                          minWidth: 260,
                          borderRight: `2px solid ${theme.palette.divider}`,
                          background: theme.palette.grey[50],
                          fontWeight: 500,
                        }}
                      >
                        {Object.values(row.cells)[0]?.GrpName1 || row.rowLabel || row.name}
                      </TableCell>
                      {diagramData.columns.map(({ name: columnName, groupLayerName }, idx, arr) => {
                        const cell = row.cells[`${columnName}__${groupLayerName || ""}`];

                        return (
                          <TableCell
                            key={`${columnName}__${groupLayerName || ""}`}
                            sx={{
                              borderRight: groupLastIndexes.includes(idx)
                                ? `2px solid ${theme.palette.divider}`
                                : idx !== arr.length - 1
                                  ? `1px solid ${theme.palette.grey[300]}`
                                  : undefined,
                              backgroundColor: cell?.AlarmStatus
                                ? resolveAlarmColor(cell?.AlarmStatus || 0)
                                : cell?.PaletteBackgroundColor
                                  ? palette[parseInt(cell?.PaletteBackgroundColor || "1") - 1]
                                  : "#FFF",
                            }}
                          >
                            {cell?.Value !== undefined ? `${cell?.Value}` : "-"}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};
